import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../../context/AuthContext';

function HomeTab({
  categories,
  tasks,
  collapsedPhases,
  setCollapsedPhases,
  handleAddCategory,
  setEditCategoryModal,
  openTaskModal,
  toggleDrawer,
  onReorderTasks,
  onReorderCategories,
  isProjectMaster, // 최고 권한 여부
  canCreateTask    // 세부업무 추가 권한 (마스터 + 일반팀원)
}) {
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [dragEnabledTaskId, setDragEnabledTaskId] = useState(null); // 특정 손잡이에서만 드래그 허용
  const [hoveredAssigneeTaskId, setHoveredAssigneeTaskId] = useState(null); // 커스텀 호버 툴팁 상태
  const { user } = useContext(AuthContext);

  const handleDragStart = (e, task) => {
    e.stopPropagation();
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  const [dragOverEmptyCategoryId, setDragOverEmptyCategoryId] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState('before');

  // 제자리(원래 위치)로의 무의미한 이동인지 판별하는 함수
  const isNoopPosition = (dragged, target, position) => {
    if (!dragged || !target) return false;
    if (String(dragged.category_id) !== String(target.category_id)) return false;

    const sameCatTasks = tasks.filter(t => String(t.category_id) === String(dragged.category_id));
    const draggedIndex = sameCatTasks.findIndex(t => t.id === dragged.id);
    const targetIndex = sameCatTasks.findIndex(t => t.id === target.id);

    if (draggedIndex === -1 || targetIndex === -1) return false;

    // 1) 바로 다음 카드의 앞(왼쪽) = 자기 원래 자리
    if (targetIndex === draggedIndex + 1 && position === 'before') return true;

    // 2) 바로 이전 카드의 뒤(오른쪽) = 자기 원래 자리
    if (targetIndex === draggedIndex - 1 && position === 'after') return true;

    return false;
  };

  const handleDragOver = (e, task) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedTask && draggedTask.id !== task.id) {
      const rect = e.currentTarget.getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      const position = e.clientX > midX ? 'after' : 'before';

      // 제자리 이동인 경우 가이드선 표시 안 함
      if (isNoopPosition(draggedTask, task, position)) {
        setDragOverTaskId(null);
        setDragOverPosition('before');
        return;
      }

      setDragOverTaskId(task.id);
      setDragOverPosition(position);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = (e) => {
    if (e) e.stopPropagation();
    setDragOverTaskId(null);
    setDragOverPosition('before');
  };

  const handleDrop = (e, targetTask) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTaskId(null);
    if (!draggedTask) return;
    if (draggedTask.id === targetTask.id) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const position = e.clientX > midX ? 'after' : 'before';

    // 제자리 이동인 경우 불필요한 API 호출 차단
    if (isNoopPosition(draggedTask, targetTask, position)) {
      setDraggedTask(null);
      setDragOverPosition('before');
      return;
    }

    if (onReorderTasks) {
      onReorderTasks(draggedTask.category_id, draggedTask.id, targetTask.id, targetTask.category_id, position);
    }
    setDraggedTask(null);
    setDragOverPosition('before');
  };

  const handleEmptyCategoryDragOver = (e, categoryId) => {
    if (draggedTask && String(draggedTask.category_id) !== String(categoryId)) {
      e.preventDefault();
      e.stopPropagation();
      setDragOverEmptyCategoryId(categoryId);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleEmptyCategoryDragLeave = (e) => {
    if (e) e.stopPropagation();
    setDragOverEmptyCategoryId(null);
  };

  const handleEmptyCategoryDrop = (e, targetCategoryId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverEmptyCategoryId(null);
    if (!draggedTask) return;
    if (String(draggedTask.category_id) === String(targetCategoryId)) return;

    if (onReorderTasks) {
      onReorderTasks(draggedTask.category_id, draggedTask.id, null, targetCategoryId, 'after');
    }
    setDraggedTask(null);
    setDragOverPosition('before');
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverTaskId(null);
    setDragEnabledTaskId(null);
    setDragOverEmptyCategoryId(null);
    setDragOverPosition('before');
  };

  // --- Category Drag & Drop ---
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [dragOverCategoryId, setDragOverCategoryId] = useState(null);
  const [dragEnabledCategoryId, setDragEnabledCategoryId] = useState(null);

  const handleCategoryDragStart = (e, category) => {
    setDraggedCategory(category);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `category_${category.id}`);
  };

  const handleCategoryDragOver = (e, category) => {
    e.preventDefault();
    if (draggedCategory && draggedCategory.id !== category.id) {
      setDragOverCategoryId(category.id);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleCategoryDragLeave = () => {
    setDragOverCategoryId(null);
  };

  const handleCategoryDrop = (e, targetCategory) => {
    e.preventDefault();
    setDragOverCategoryId(null);
    if (!draggedCategory) return;
    if (draggedCategory.id === targetCategory.id) return;

    if (onReorderCategories) {
      onReorderCategories(draggedCategory.id, targetCategory.id);
    }
    setDraggedCategory(null);
  };

  const handleCategoryDragEnd = () => {
    setDraggedCategory(null);
    setDragOverCategoryId(null);
    setDragEnabledCategoryId(null);
  };

  // --- Robust Auto Scroll on Drag ---
  useEffect(() => {
    let scrollInterval = null;
    let currentY = 0;

    const handleGlobalDragOver = (e) => {
      currentY = e.clientY;
    };

    if (draggedCategory || draggedTask) {
      document.addEventListener('dragover', handleGlobalDragOver);

      scrollInterval = setInterval(() => {
        if (currentY === 0) return;

        const threshold = 100; // 화면 가장자리 인식 범위 (px)
        const scrollSpeed = 35; // 스크롤 속도

        // 브라우저 화면(Viewport) 위/아래 가장자리에 마우스가 위치하면 전체 화면 스크롤
        if (currentY < threshold) {
          window.scrollBy(0, -scrollSpeed);
        } else if (window.innerHeight - currentY < threshold) {
          window.scrollBy(0, scrollSpeed);
        }
      }, 50);
    }

    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [draggedCategory, draggedTask]);

  return (
    <div className="animation-fade-in">
      <style>{`
        .task-card-hover {
          transition: transform 0.2s ease, box-shadow 0.2s ease !important;
        }
        .task-card-hover:hover {
          transform: translateY(-3px) !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
        }
      `}</style>
      {categories.length === 0 ? (
        <div className="text-center py-5 bg-white rounded shadow-sm border">
          <i className="fas fa-layer-group fa-3x text-light mb-3"></i>
          <h5 className="text-muted fw-bold mb-4">등록된 분류(Phase)가 없습니다.</h5>
          {isProjectMaster && (
            <button className="btn btn-primary fw-bold px-4 shadow-sm" onClick={handleAddCategory}>
              <i className="fas fa-plus me-2"></i>첫 번째 분류 추가
            </button>
          )}
        </div>
      ) : (
        <div className="pb-2">
          {categories.map(category => {
            const categoryTasks = tasks.filter(t => t.category_id === category.id);
            const forceCollapseEmpty = !canCreateTask && categoryTasks.length === 0;
            const isCollapsed = forceCollapseEmpty ? true : collapsedPhases[category.id];
            return (
              <div key={category.id} className="mb-3 bg-white p-3 rounded shadow-sm border"
                draggable={dragEnabledCategoryId === category.id}
                onDragStart={(e) => handleCategoryDragStart(e, category)}
                onDragOver={(e) => handleCategoryDragOver(e, category)}
                onDragLeave={handleCategoryDragLeave}
                onDrop={(e) => handleCategoryDrop(e, category)}
                onDragEnd={handleCategoryDragEnd}
                style={{
                  borderColor: dragOverCategoryId === category.id ? '#0d6efd' : '#eef0f2',
                  opacity: draggedCategory?.id === category.id ? 0.4 : 1,
                  borderStyle: dragOverCategoryId === category.id ? 'dashed' : 'solid',
                  borderWidth: dragOverCategoryId === category.id ? '2px' : '1px',
                  transform: dragOverCategoryId === category.id ? 'scale(1.01)' : 'none',
                  boxShadow: dragOverCategoryId === category.id ? '0 0.5rem 1rem rgba(13, 110, 253, 0.15)' : 'none',
                  zIndex: dragOverCategoryId === category.id ? 10 : 1,
                  position: dragOverCategoryId === category.id ? 'relative' : 'static',
                  transition: 'all 0.2s ease'
                }}>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2" style={{ borderBottom: '1px solid #adb5bd' }}>
                  <div className="d-flex align-items-center">
                    <div
                      className="text-muted me-3 d-flex align-items-center justify-content-center"
                      style={{ cursor: isProjectMaster ? (dragEnabledCategoryId === category.id ? 'grabbing' : 'grab') : 'default', width: '20px', height: '24px', opacity: isProjectMaster ? 1 : 0.3 }}
                      onMouseEnter={() => isProjectMaster && setDragEnabledCategoryId(category.id)}
                      onMouseLeave={() => setDragEnabledCategoryId(null)}
                      title={isProjectMaster ? "드래그하여 분류 순서 변경" : "권한 없음"}>
                      <i className={`fas fa-grip-vertical fs-5 ${dragEnabledCategoryId === category.id ? 'text-danger' : 'text-secondary'}`} style={{ color: dragEnabledCategoryId === category.id ? '#ff6b81' : '' }}></i>
                    </div>
                    <h5 className="fw-bolder text-dark mb-0 d-flex align-items-center" style={{ cursor: forceCollapseEmpty ? 'default' : 'pointer', opacity: forceCollapseEmpty ? 0.6 : 1 }} onClick={() => { if (!forceCollapseEmpty) setCollapsedPhases(prev => ({ ...prev, [category.id]: !prev[category.id] })) }}>
                      <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'} me-2 text-primary`} style={{ width: '16px', fontSize: '1rem', opacity: forceCollapseEmpty ? 0.5 : 1 }}></i>
                      {category.name}
                      <span className="badge bg-light text-secondary ms-3 border px-2 py-1 fs-8">{categoryTasks.length}</span>
                    </h5>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    {isProjectMaster && (
                      <button className="btn btn-sm btn-light border text-muted px-2" title="분류 수정" onClick={() => setEditCategoryModal({ isOpen: true, categoryId: category.id, name: category.name, sortOrder: category.sort_order ?? 10 })}>
                        <i className="fas fa-pencil-alt"></i>
                      </button>
                    )}
                    {canCreateTask && (
                      <button className="btn btn-sm btn-primary fw-bold px-3 shadow-sm" title="새 업무 추가" onClick={() => openTaskModal(category.id)}>
                        <i className="fas fa-plus me-1"></i>세부업무 추가
                      </button>
                    )}
                  </div>
                </div>

                {!isCollapsed && (
                  <div className="row g-3">
                    {categoryTasks.length === 0 ? (
                      <div
                        className="col-12 text-center text-muted p-4 bg-light rounded"
                        style={{
                          border: dragOverEmptyCategoryId === category.id ? '2px dashed #0d6efd' : '1px dashed #ccc',
                          backgroundColor: dragOverEmptyCategoryId === category.id ? '#e7f1ff' : undefined,
                          transition: 'all 0.2s ease'
                        }}
                        onDragOver={(e) => handleEmptyCategoryDragOver(e, category.id)}
                        onDragLeave={handleEmptyCategoryDragLeave}
                        onDrop={(e) => handleEmptyCategoryDrop(e, category.id)}
                      >
                        {canCreateTask ? (
                          <>이 단계에 아직 업무가 없습니다. 세부업무를 이곳으로 드래그하거나 우측 상단의 <span className="fw-bold">세부업무 추가</span> 버튼을 누르세요.</>
                        ) : (
                          <>이 단계에 아직 등록된 세부업무가 없습니다.</>
                        )}
                      </div>
                    ) : (
                      categoryTasks.map(task => {
                        const allMembers = task.members || [];
                        const assignees = allMembers
                          .filter(m => m.is_manager === true || m.is_manager === 1)
                          .sort((a, b) => (a.user?.name || a.user_id || '').localeCompare(b.user?.name || b.user_id || ''));
                        const watchers = allMembers.filter(m => m.is_manager === false || m.is_manager === 0);

                        let primaryAssignee = '담당자 미지정';
                        let extraCount = 0;

                        if (assignees.length > 0) {
                          // 본인이 포함되어 있다면 본인을 맨 앞으로 당김
                          const meIndex = assignees.findIndex(a => String(a.user_id) === String(user?.id) || String(a.user_id) === String(user?.userid));
                          const firstMember = meIndex >= 0 ? assignees[meIndex] : assignees[0];
                          const firstName = firstMember.user?.name || firstMember.user_id || '알수없음';
                          const firstPosition = firstMember.user?.org?.position || '';
                          primaryAssignee = firstPosition ? `${firstName}/${firstPosition}` : firstName;
                          extraCount = assignees.length - 1;
                        }

                        const isExplicitWatcher = watchers.some(w => 
                          String(w.user_id) === String(user?.id) || 
                          String(w.user_id) === String(user?.userid) ||
                          (w.user && String(w.user.id) === String(user?.id)) ||
                          (w.user && String(w.user.userid) === String(user?.userid))
                        );
                        const isPureWatcher = !canCreateTask && isExplicitWatcher;

                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const taskEndDate = task.end_date ? new Date(task.end_date.split('T')[0]) : null;
                        const isDelayed = taskEndDate && taskEndDate < today && (task.progress_rate || 0) < 100;

                        return (
                          <div className="col-md-4 col-lg-3" key={task.id}>
                            <div className="card shadow-sm border task-card-hover mb-2"
                              draggable={isProjectMaster && dragEnabledTaskId === task.id}
                              onDragStart={(e) => handleDragStart(e, task)}
                              onDragOver={(e) => handleDragOver(e, task)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, task)}
                              onDragEnd={handleDragEnd}
                              style={{
                                position: 'relative',
                                opacity: draggedTask?.id === task.id ? 0.5 : 1,
                                transform: dragOverTaskId === task.id ? 'scale(1.02)' : undefined,
                                transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
                                zIndex: dragOverTaskId === task.id ? 10 : 1,
                                border: dragOverTaskId === task.id ? '1px solid #a5b4fc' : '1px solid #ced4da',
                                borderLeft: `4px solid ${task.status === 'completed' ? '#20c997' : (isDelayed ? '#dc3545' : (task.status === 'in_progress' ? '#0d6efd' : '#ffc107'))}`,
                                boxShadow: dragOverTaskId === task.id ? '0 0.25rem 0.75rem rgba(79, 70, 229, 0.2)' : undefined,
                                cursor: isPureWatcher ? 'default' : 'pointer'
                              }}
                              onClick={() => {
                                if (!isPureWatcher) {
                                  toggleDrawer(task);
                                }
                              }}>
                              {/* 🎯 드롭 위치 직관적 가이드 바 (1px 슬림 모던 인디고 스타일) */}
                              {dragOverTaskId === task.id && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '-3px',
                                    bottom: '-3px',
                                    left: dragOverPosition === 'before' ? '-3px' : undefined,
                                    right: dragOverPosition === 'after' ? '-3px' : undefined,
                                    width: '1px',
                                    backgroundColor: '#4f46e5',
                                    boxShadow: '0 0 4px rgba(79, 70, 229, 0.6)',
                                    zIndex: 25,
                                    pointerEvents: 'none'
                                  }}
                                >
                                  {/* 위/아래 7px 미니 앵커 점 (1px 선 기준 완벽 중앙 정렬: left -3px) */}
                                  <div style={{ position: 'absolute', top: '-3.5px', left: '-3px', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#4f46e5' }} />
                                  <div style={{ position: 'absolute', bottom: '-3.5px', left: '-3px', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#4f46e5' }} />
                                </div>
                              )}
                              <div className="card-body p-2 d-flex flex-column bg-light rounded" style={{ transition: 'all 0.2s ease' }}>

                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="d-flex align-items-center flex-wrap gap-1">
                                    <span className={`badge ${task.status === 'completed' ? 'bg-success' : (isDelayed ? 'bg-danger text-white' : (task.status === 'in_progress' ? 'bg-primary' : 'bg-warning text-dark'))} px-2 py-1 shadow-sm`}>
                                      {task.status === 'completed' ? '완료' : (isDelayed ? '지연' : (task.status === 'in_progress' ? '진행중' : '시작전'))}
                                    </span>
                                    {task.priority === 'high' && <span className="badge bg-danger px-2 py-1 shadow-sm"><i className="fas fa-arrow-up"></i></span>}
                                    <small className="text-muted fw-bold ms-1" style={{ fontSize: '0.7rem' }}>
                                      <i className="far fa-calendar-alt me-1"></i>
                                      {task.start_date ? task.start_date.split('T')[0] : '미정'} ~ {task.end_date ? task.end_date.split('T')[0] : '미정'}
                                    </small>
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{ cursor: isProjectMaster ? (dragEnabledTaskId === task.id ? 'grabbing' : 'grab') : 'default', opacity: isProjectMaster ? 1 : 0.3 }}
                                    onMouseEnter={() => isProjectMaster && setDragEnabledTaskId(task.id)}
                                    onMouseLeave={() => setDragEnabledTaskId(null)}
                                    onClick={(e) => { e.stopPropagation(); }}
                                    title={isProjectMaster ? "드래그하여 이동" : "권한 없음"}>
                                    <i className={`fas fa-grip-vertical px-2 ${dragEnabledTaskId === task.id ? 'text-danger' : 'text-secondary'}`} style={{ color: dragEnabledTaskId === task.id ? '#ff6b81' : '' }}></i>
                                  </div>
                                </div>

                                {/* 2. 중앙 (Middle) */}
                                <h6 className="fw-bolder text-dark mb-1 lh-base text-truncate" style={{ fontSize: '0.8rem', cursor: isPureWatcher ? 'default' : 'pointer' }} title={task.title}>{task.title}</h6>

                                {/* 진척도 바 */}
                                <div className="mb-2">
                                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.75rem' }}>
                                    <span className="text-muted fw-bold"><i className="fas fa-check-square me-1"></i>진척도</span>
                                    <span className="text-primary fw-bold">{task.progress_rate || 0}%</span>
                                  </div>
                                  <div className="progress" style={{ height: '6px' }}>
                                    <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${task.progress_rate || 0}%` }}></div>
                                  </div>
                                </div>

                                {/* 3. 하단 (Bottom) */}
                                <div className="border-top pt-2 d-flex justify-content-between align-items-center" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                                  <div className="d-flex align-items-center gap-3">
                                      {/* 체크리스트 아이콘 */}
                                      {(task.checklist_count || task.instructions_count || 0) >= 0 && (
                                        <small
                                          className="text-muted d-flex align-items-center"
                                          title={isPureWatcher ? "권한 없음" : "체크리스트 보기"}
                                          style={{ cursor: isPureWatcher ? 'default' : 'pointer', transition: 'color 0.2s' }}
                                          onMouseEnter={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-muted', 'text-primary'); }}
                                          onMouseLeave={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-primary', 'text-muted'); }}
                                          onClick={(e) => {
                                            if (!isPureWatcher && toggleDrawer) {
                                              e.stopPropagation();
                                              toggleDrawer(task, 'checklist');
                                            }
                                          }}
                                        >
                                          <i className="fas fa-tasks me-1"></i>{task.checklist_count || task.instructions_count || 0}
                                        </small>
                                      )}

                                      {/* 코멘트 아이콘 */}
                                      {(task.comments_count || 0) >= 0 && (
                                        <small
                                          className="text-muted d-flex align-items-center"
                                          title={isPureWatcher ? "권한 없음" : "코멘트 보기"}
                                          style={{ cursor: isPureWatcher ? 'default' : 'pointer', transition: 'color 0.2s' }}
                                          onMouseEnter={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-muted', 'text-primary'); }}
                                          onMouseLeave={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-primary', 'text-muted'); }}
                                          onClick={(e) => {
                                            if (!isPureWatcher && toggleDrawer) {
                                              e.stopPropagation();
                                              toggleDrawer(task, 'comments');
                                            }
                                          }}
                                        >
                                          <i className="far fa-comment-dots me-1"></i>{task.comments_count || 0}
                                        </small>
                                      )}

                                      {/* 첨부파일 아이콘 */}
                                      {(task.files_count || 0) >= 0 && (
                                        <small
                                          className="text-muted d-flex align-items-center"
                                          title={isPureWatcher ? "권한 없음" : "첨부파일 보기"}
                                          style={{ cursor: isPureWatcher ? 'default' : 'pointer', transition: 'color 0.2s' }}
                                          onMouseEnter={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-muted', 'text-primary'); }}
                                          onMouseLeave={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-primary', 'text-muted'); }}
                                          onClick={(e) => {
                                            if (!isPureWatcher && toggleDrawer) {
                                              e.stopPropagation();
                                              toggleDrawer(task, 'files');
                                            }
                                          }}
                                        >
                                          <i className="fas fa-paperclip me-1"></i>{task.files_count || 0}
                                        </small>
                                      )}

                                      {/* 세부설정 아이콘 */}
                                      <small
                                        className="text-muted d-flex align-items-center ms-1"
                                        title={isPureWatcher ? "권한 없음" : "세부설정"}
                                        style={{ cursor: isPureWatcher ? 'default' : 'pointer', transition: 'color 0.2s' }}
                                        onMouseEnter={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-muted', 'text-primary'); }}
                                        onMouseLeave={(e) => { if (!isPureWatcher) e.currentTarget.classList.replace('text-primary', 'text-muted'); }}
                                        onClick={(e) => {
                                          if (!isPureWatcher && toggleDrawer) {
                                            e.stopPropagation();
                                            toggleDrawer(task, 'settings');
                                          }
                                        }}
                                      >
                                        <i className="fas fa-cog"></i>
                                      </small>
                                    </div>

                                    <div className="position-relative d-flex align-items-center"
                                      onMouseEnter={() => setHoveredAssigneeTaskId(task.id)}
                                      onMouseLeave={() => setHoveredAssigneeTaskId(null)}>

                                      <small className="text-dark fw-bold text-truncate" style={{ maxWidth: '140px', cursor: (assignees.length + watchers.length) > 1 ? 'pointer' : 'default' }}>
                                        <i className="fas fa-user-circle text-primary me-1" style={{ fontSize: '1.1em' }}></i>
                                        {primaryAssignee} {extraCount > 0 && <span className="text-muted fw-normal fs-8">외 {extraCount}명</span>}
                                      </small>

                                      {/* 커스텀 호버 레이어 툴팁 (고급스러운 다크 테마 & 슬림 사이즈) */}
                                      {hoveredAssigneeTaskId === task.id && (assignees.length + watchers.length > 1) && (
                                        <div className="position-absolute shadow-lg rounded-3 p-2"
                                          style={{
                                            bottom: '100%', right: '0', width: 'max-content', maxWidth: '250px',
                                            marginBottom: '8px', zIndex: 1050, backgroundColor: '#343a40',
                                            color: '#f8f9fa', pointerEvents: 'none'
                                          }}>
                                          {/* 말풍선 꼬리 */}
                                          <div style={{
                                            position: 'absolute', bottom: '-4px', right: '20px',
                                            width: '8px', height: '8px', backgroundColor: '#343a40',
                                            transform: 'rotate(45deg)'
                                          }}></div>

                                          {assignees.length > 0 && (
                                            <div className="mb-2 position-relative">
                                              <div className="mb-1">
                                                <span className="badge bg-primary px-1 border border-primary border-opacity-50" style={{ fontSize: '0.65rem' }}>담당자</span>
                                              </div>
                                              <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                                {[...assignees].sort((a, b) => (a.user?.name || a.user_id || '').localeCompare(b.user?.name || b.user_id || '')).map(m => {
                                                  const n = m.user?.name || m.user_id || '알수없음';
                                                  const p = m.user?.org?.position || '';
                                                  const displayName = p ? `${n}/${p}` : n;
                                                  return (
                                                    <span key={m.user_id || m.id} className="rounded"
                                                      style={{ backgroundColor: 'rgba(255,255,255,0.15)', padding: '2px 6px', fontSize: '0.7rem', wordBreak: 'keep-all', color: '#e9ecef' }}>
                                                      {displayName}
                                                    </span>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}
                                          {watchers.length > 0 && (
                                            <div className="position-relative">
                                              <div className="mb-1">
                                                <span className="badge bg-info text-dark px-1 border border-info border-opacity-50" style={{ fontSize: '0.65rem' }}>참조자</span>
                                              </div>
                                              <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                                {[...watchers].sort((a, b) => (a.user?.name || a.user_id || '').localeCompare(b.user?.name || b.user_id || '')).map(m => {
                                                  const n = m.user?.name || m.user_id || '알수없음';
                                                  const p = m.user?.org?.position || '';
                                                  const displayName = p ? `${n}/${p}` : n;
                                                  return (
                                                    <span key={m.user_id || m.id} className="rounded"
                                                      style={{ backgroundColor: 'rgba(255,255,255,0.15)', padding: '2px 6px', fontSize: '0.7rem', wordBreak: 'keep-all', color: '#ced4da' }}>
                                                      {displayName}
                                                    </span>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                </div>

                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-center mt-4 mb-2">
            {isProjectMaster && (
              <button className="btn btn-outline-secondary fw-bold px-5 py-2 shadow-sm rounded-pill" onClick={handleAddCategory}>
                <i className="fas fa-plus me-2"></i>새로운 분류 추가
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeTab;
